import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { graphql, useFragment } from 'react-relay';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { fg } from '@atlassian/jira-feature-gating';
import { useColumnPickerOpenState } from '@atlassian/jira-issue-table-column-picker/src/controllers/column-picker-open-state/index.tsx';
import AsyncColumnPicker from '@atlassian/jira-issue-table-column-picker/src/ui/async.tsx';
import type {
	AsyncLoadOptions,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { MODIFY_COLUMNS } from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import type {
	ColumnConfigChangeType,
	ColumnId,
	OptimisticFieldSets,
} from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import type { columnPicker_issueViewChildIssuesPanel$key } from '@atlassian/jira-relay/src/__generated__/columnPicker_issueViewChildIssuesPanel.graphql.ts';

export interface Props {
	columns: columnPicker_issueViewChildIssuesPanel$key;
	useColumnLoaderProp: (selectedColumns: SelectValue) => AsyncLoadOptions;
	isLoading?: boolean;
	onColumnsChange?: (
		columnIds: ColumnId[],
		changeType: ColumnConfigChangeType,
		optimisticFieldSets?: OptimisticFieldSets,
	) => void;
	onRestoreDefaultColumns?: () => void;
	isMergedColumnEnabled?: boolean;
	isVisualUpliftForChildIssuesTableExpEnabled?: boolean;
}

export interface ColumnPickerApi {
	close: () => void;
}

export const ColumnPicker = forwardRef<ColumnPickerApi, Props>((props, columnPickerRef) => {
	const {
		columns,
		isLoading = false,
		isMergedColumnEnabled,
		onColumnsChange,
		onRestoreDefaultColumns,
		useColumnLoaderProp,
	} = props;

	const [, { close }] = useColumnPickerOpenState();

	useImperativeHandle(columnPickerRef, () => ({ close }), [close]);

	const columnsData = useFragment<columnPicker_issueViewChildIssuesPanel$key>(
		graphql`
			fragment columnPicker_issueViewChildIssuesPanel on JiraIssueSearchFieldSetConnection {
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...main_issueTableColumnPicker_ColumnConfig
			}
		`,
		columns,
	);

	// on cleanup of fg('jira_inline_field_config_gate'), param type should just be { columnIds: string[] }
	const onColumnPickerChange = useCallback(
		(columnIds: string[] | undefined | { columnIds: string[] }) => {
			if (
				onColumnsChange &&
				columnIds != null &&
				'columnIds' in columnIds &&
				fg('jira_inline_field_config_gate')
			) {
				onColumnsChange(columnIds.columnIds, MODIFY_COLUMNS);
			} else if (onColumnsChange && columnIds && Array.isArray(columnIds)) {
				onColumnsChange(columnIds, MODIFY_COLUMNS);
			}
		},
		[onColumnsChange],
	);

	return (
		<AsyncColumnPicker
			columns={columnsData}
			components={{
				Icon: CustomizeIcon,
			}}
			isLoading={isLoading}
			onChange={onColumnPickerChange}
			onRestoreDefaultColumns={onRestoreDefaultColumns}
			useColumnLoader={useColumnLoaderProp}
			isMergedColumnEnabled={isMergedColumnEnabled && fg('jira_list_merged_column_gate')}
		/>
	);
});
